import remarkBreaks from 'remark-breaks';
import remarkGfm from 'remark-gfm';
import remarkParse from 'remark-parse';
import remarkStringify from 'remark-stringify';
import { unified } from 'unified';
import { normalizeNoteMdast } from './note-mdast.js';
/**
 * **How a note's text is written** — the editor's spelling, for the editor and
 * for whatever else writes a note (#67).
 *
 * The editor serializes through Milkdown, which is remark-stringify with these
 * settings (`apps/web/src/components/note-editor.tsx`); `noteMarkdown` below is
 * the same remark-stringify with the same settings, for a note that does not
 * come out of the editor — the text of a Lemniscus export. One definition, so
 * the two cannot drift apart.
 */
/**
 * A line break: a newline, which `remark-breaks` reads back as one — except in
 * a table cell, which cannot hold a real one in GFM, so there it is `<br>`
 * (#29), the one place the note's language admits that tag (see the storage
 * format in `.claude/rules/notes.md`). The GFM table serializer marks a cell as
 * the `tableCell` construct, which is how the handler knows where it stands.
 */
function writeBreak(_node, _parent, state) {
    return state.stack.includes('tableCell') ? '<br>' : '\n';
}
/** The stringifier's settings, and both of them were measured rather than
 *  guessed (see `note-editor.tsx`): without `bullet` every `-` becomes `*`,
 *  and without `rule` every `---` becomes `***` — on the first save, in every
 *  note that already had one. */
export const NOTE_STRINGIFY_OPTIONS = {
    bullet: '-',
    rule: '-',
    handlers: { break: writeBreak },
};
const reader = unified().use(remarkParse).use(remarkGfm).use(remarkBreaks);
const writer = unified().use(remarkGfm).use(remarkStringify, NOTE_STRINGIFY_OPTIONS);
/** A tree of the note's language as the editor writes it. */
export function noteMarkdown(tree) {
    return String(writer.stringify(tree));
}
/**
 * **Markdown written elsewhere, as a note's text — without losing a word**
 * (#67). Read as any note is read — GFM, a single newline a line break — then
 * brought into the note's language by the normalizer and written as the editor
 * writes. Where the normalizer would *drop* a construct, its words are kept
 * first, in the nearest form the language has:
 *
 * - **raw HTML** stays as the text it is, tags and all — `<u>Wort</u>` reads as
 *   written, it does not become underlined (a `<br>` in a table cell is the
 *   language's own line break, as everywhere);
 * - **an image** becomes a link to it, its alt text the link's text;
 * - **a reference** (`[text][ref]`, `![alt][ref]`) becomes the link its
 *   definition names; a definition nothing refers to stays as its line;
 * - **a footnote** stays as `[^1]`, its definition as a paragraph `[^1]: …`.
 *
 * What the normalizer flattens rather than drops keeps its words anyway: a
 * heading below level three is lifted to level three. Nothing here knows
 * Lemniscus; the input is Markdown and nothing else. Never throws.
 */
export function noteTextFromMarkdown(text) {
    // The reader's own plugins only; what comes out is still an mdast root.
    const tree = reader.runSync(reader.parse(text));
    keepWords(tree);
    return noteMarkdown(normalizeNoteMdast(tree)).trim();
}
const FLOW_PARENTS = new Set([
    'root',
    'blockquote',
    'listItem',
    'footnoteDefinition',
]);
/** `<br>`, `<br/>`, `<br />` — the normalizer's own line break in a cell. */
const CELL_BREAK = /^<br\s*\/?>$/i;
const textNode = (value) => ({ type: 'text', value });
function typeOf(node) {
    return String(node.type ?? '');
}
function childrenOf(node) {
    const children = node.children;
    return Array.isArray(children) ? children : null;
}
/** The way GFM compares labels: case and runs of whitespace do not count. */
function labelKey(identifier) {
    return identifier.toLowerCase().replace(/\s+/g, ' ').trim();
}
function keepWords(tree) {
    const definitions = new Map();
    const collect = (node) => {
        if (typeOf(node) === 'definition') {
            const definition = node;
            definitions.set(labelKey(definition.identifier), definition);
        }
        for (const child of childrenOf(node) ?? [])
            collect(child);
    };
    collect(tree);
    const used = new Set();
    rewrite(tree, definitions, used);
    dropUsedDefinitions(tree, used);
}
function rewrite(parent, definitions, used) {
    const flow = FLOW_PARENTS.has(parent.type);
    const children = [];
    for (const child of parent.children) {
        const type = child.type;
        let replacement = [child];
        if (type === 'html') {
            const value = child.value;
            if (!(parent.type === 'tableCell' && CELL_BREAK.test(value.trim()))) {
                replacement = [flow ? paragraphOf([textNode(value)]) : textNode(value)];
            }
        }
        else if (type === 'image') {
            replacement = [linkTo(child.url, child.title ?? null, child.alt || child.url)];
        }
        else if (type === 'imageReference' || type === 'linkReference') {
            const key = labelKey(child.identifier);
            const definition = definitions.get(key);
            if (definition) {
                used.add(key);
                replacement =
                    type === 'imageReference'
                        ? [linkTo(definition.url, definition.title ?? null, child.alt || definition.url)]
                        : [
                            {
                                type: 'link',
                                url: definition.url,
                                title: definition.title ?? null,
                                children: child.children,
                            },
                        ];
            }
        }
        else if (type === 'footnoteReference') {
            replacement = [textNode(`[^${child.label ?? child.identifier}]`)];
        }
        else if (type === 'footnoteDefinition') {
            const label = `[^${child.label ?? child.identifier}]: `;
            const [first, ...rest] = child.children;
            replacement =
                first?.type === 'paragraph'
                    ? [paragraphOf([textNode(label), ...first.children]), ...rest]
                    : [paragraphOf([textNode(label.trimEnd())]), ...child.children];
        }
        for (const node of replacement) {
            if (childrenOf(node))
                rewrite(node, definitions, used);
            children.push(node);
        }
    }
    ;
    parent.children = children;
}
/** A definition whose reference became a link has said what it had to say;
 *  one nothing refers to stays as the line it was. */
function dropUsedDefinitions(parent, used) {
    const children = [];
    for (const child of parent.children) {
        if (child.type === 'definition') {
            if (used.has(labelKey(child.identifier)))
                continue;
            // The address as the link it is — written as text, GFM would read it
            // back as one anyway, and the text would not survive a second pass.
            children.push(paragraphOf([
                textNode(`[${child.label ?? child.identifier}]: `),
                linkTo(child.url, child.title ?? null, child.url),
                ...(child.title ? [textNode(` "${child.title}"`)] : []),
            ]));
            continue;
        }
        if (childrenOf(child))
            dropUsedDefinitions(child, used);
        children.push(child);
    }
    ;
    parent.children = children;
}
function paragraphOf(children) {
    return { type: 'paragraph', children };
}
function linkTo(url, title, text) {
    return { type: 'link', url, title, children: [textNode(text)] };
}
//# sourceMappingURL=note-markdown.js.map